import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Loader2 } from 'lucide-react';

import { apiService } from '../../services/apiService';
import { CustomerSearchResult } from '../../services/types';

interface CustomerSearchSelectProps {
  onSelect: (customer: CustomerSearchResult) => void;
  selectedLabel?: string | null;
}

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

/**
 * Searchable customer dropdown. Debounces input, queries the customer search
 * endpoint, and lists matches (name + customer ID) that can be selected.
 */
export const CustomerSearchSelect: React.FC<CustomerSearchSelectProps> = ({
  onSelect,
  selectedLabel,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<CustomerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = useCallback(async (term: string) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.searchCustomers(term);
      // Ignore out-of-order responses
      if (requestId === requestIdRef.current) {
        setResults(data);
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Debounced search on input change
  useEffect(() => {
    const term = inputValue.trim();
    if (term.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    const handle = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [inputValue, runSearch]);

  const handleSelect = (customer: CustomerSearchResult) => {
    onSelect(customer);
    setInputValue('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={
            selectedLabel || t('customerDashboard.searchPlaceholder')
          }
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-3 w-5 h-5 text-gray-400 animate-spin" />
        )}
        {!loading && inputValue && (
          <button
            onClick={() => {
              setInputValue('');
              setResults([]);
            }}
            className="absolute right-3 top-2.5 p-0.5 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {open && (inputValue.trim().length >= MIN_CHARS || error) && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {error && (
            <div className="px-4 py-3 text-sm text-red-600">{error}</div>
          )}
          {!error && !loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {t('customerDashboard.noResults')}
            </div>
          )}
          {results.map((customer) => (
            <button
              key={customer.custNum}
              onClick={() => handleSelect(customer)}
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition flex items-center justify-between gap-3 border-b border-gray-100 last:border-b-0"
            >
              <span className="font-medium text-gray-900 truncate">
                {customer.name}
              </span>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {customer.custId}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
